/**
 * A vertical's own list in Karr's sidebar (docs/VERTICALS_ROADMAP.md §1, law 2,
 * founder 23 Oct 2026: "same design language and UI UX as Karr, the sidebar").
 *
 * A vertical keeps Karr's shell: the header with its figures, the fill rail,
 * the floating Back, the sidebar toggle at the centre of the left edge. The
 * one thing it changes is the sidebar's list. Its area (a lazy chunk under the
 * app layout) hands its list here while one of its pages is open, and
 * AppLayout draws that list in the same Sidebar instead of the books' zones.
 * The vertical's role decides the list (a counter clerk sees the counter's
 * places, never eighty rows of a books product), so the list is built where
 * the role is known, never in the first load.
 *
 * The list holds only while the route stays under the vertical's prefix: a
 * link out to Books shows the books' sidebar at once, even before the area
 * unmounts.
 */
import { create } from 'zustand';
import type { NavZone } from '../components/sidebarNav';

/** One zone of a vertical's list, as data: its heading and its tabs in order (vsidebar-a). */
export interface ZoneSpec<T extends string> {
    label: string;
    tabs: readonly T[];
}

export interface VerticalNav {
    /** The vertical's route, e.g. '/travel'. */
    prefix: string;
    zones: NavZone[];
    /** The vertical's short name for the way back, e.g. 'Stay' ("Back to Stay · Front desk"). */
    name?: string;
}

interface VerticalNavState {
    nav: VerticalNav | null;
    show: (nav: VerticalNav) => void;
    /** Only the vertical that set the list takes it down. */
    hide: (prefix: string) => void;
}

export const useVerticalNav = create<VerticalNavState>((set) => ({
    nav: null,
    show: (nav) => set({ nav }),
    hide: (prefix) => set((s) => (s.nav?.prefix === prefix ? { nav: null } : s)),
}));

/** The vertical's zones while the route is one of its pages; else null (the books' sidebar). */
export function verticalZonesFor(nav: VerticalNav | null, route: string): NavZone[] | null {
    if (!nav || !nav.zones.length) return null;
    return route === nav.prefix || route.startsWith(`${nav.prefix}/`) ? nav.zones : null;
}

/** Every row of a vertical's list, flat. */
function itemsOf(zones: NavZone[]) {
    return zones.flatMap((z) => z.groups.flatMap((g) => g.items));
}

/**
 * Which row of the vertical's list is open (vsidebar-a). A desk keeps its tab in the address
 * (`/stay?tab=front`), so the row is found by path AND the row's own search keys; a row with no
 * search (`/health/lab`, `/studio/classes`) by path alone. Nothing matched: the path itself, so a
 * page outside the list lights nothing.
 */
export function verticalActiveHref(zones: NavZone[], pathname: string, search: string): string {
    const now = new URLSearchParams(search);
    let bare: string | null = null;
    for (const it of itemsOf(zones)) {
        const q = it.href.indexOf('?');
        const path = q === -1 ? it.href : it.href.slice(0, q);
        if (path !== pathname) continue;
        if (q === -1) { bare ??= it.href; continue; }
        const want = new URLSearchParams(it.href.slice(q + 1));
        let all = true;
        want.forEach((v, k) => { if (now.get(k) !== v) all = false; });
        if (all) return it.href;
    }
    return bare ?? pathname;
}

/** Whether `href` is a place outside the vertical (Books, Settings, an invoice): leaving it leaves a way back. */
export function leavesVertical(nav: VerticalNav | null, href: string): boolean {
    if (!nav) return false;
    const path = href.split(/[?#]/)[0];
    return !(path === nav.prefix || path.startsWith(`${nav.prefix}/`));
}

/** What the floating Back says on the way out of a vertical: "Back to Stay · Front desk". */
export function verticalBackLabel(nav: VerticalNav, pathname: string, search: string): string {
    const name = nav.name ?? (nav.prefix.slice(1, 2).toUpperCase() + nav.prefix.slice(2));
    const href = verticalActiveHref(nav.zones, pathname, search);
    const row = itemsOf(nav.zones).find((it) => it.href === href);
    const where = row && row.label.toLowerCase() !== name.toLowerCase() ? `${name} · ${row.label}` : name;
    return `Back to ${where.length > 40 ? where.slice(0, 39) + '…' : where}`;
}

/** Who may see the header's Books button on a vertical page. */
export interface BooksButtonInput {
    /** The vertical the page belongs to. */
    prefix: string;
    /** The person's role on the open business (owner, admin, staff…). */
    orgRole?: string | null;
}

/**
 * Whether the header shows "Books" on a vertical's page. One function so lane vsidebar-b can
 * hide it for a person the business gives no Books access; until then everyone sees it.
 */
export function showBooksButton(input: BooksButtonInput): boolean {
    void input;
    return true;
}
